import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DragDrop from '@/components/DragDrop/DragDrop';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import { allExtensionsAccept, statusCampaign } from '@/helpers/constants';
import useError from '@/hooks/useError';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { setAttachmentsForms } from '@/redux/Forms/forms.action';
import { getAttachementForms } from '@/redux/Forms/forms.reducer';
import documentationServices from '@/services/documentationServices';
import { Attachment } from '@/types';
import styles from './AttachmentCollection.module.scss';
import CloudSVG from './CloudSVG';
import AttachmentFile from '../AttachmentFile/AttachmentFile';

interface AttachmentCollectionProps {
  fileList: { attachments: Attachment[] };
  checkDisable: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  deleteBlocked?: boolean;
  uploadFiles?: (files: FileList) => void;
  onFilesSelected?: (files: FileList) => void;
  setFavorite?: (id: string) => void;
  download: (id: string) => void;
  setDeleteFileId: (id: string) => void;
  isAffectedVehicles?: boolean;
  uploadProgress?: Record<string, number> | null;
  dragdropId?: string;
  inputId?: string;
  onChangeFileTags?: (id: string, field: 'jvVisible' | 'tag', value: string | boolean) => void;
  readOnlyTags?: boolean;
  maxSize: number;
  allowedFormats: string;
  readOnly: boolean;
  campaignInfo: { status: string; authorization: { readOnly: boolean } };
  setAttachments: (forms: { maxSize: number; validExtensions: string[] }) => void;
}

const AttachmentCollection: React.FC<AttachmentCollectionProps> = ({
  fileList, checkDisable, canEdit, canDelete, deleteBlocked, uploadFiles, onFilesSelected,
  setFavorite = () => { }, download, setDeleteFileId, isAffectedVehicles, maxSize,
  allowedFormats, setAttachments, campaignInfo, uploadProgress,
  dragdropId = 'attachments-dragdrop', inputId = 'addAttachment', onChangeFileTags, readOnlyTags,
}) => {
  const uploadHandler = onFilesSelected || uploadFiles || (() => null);
  const showUpload = canEdit !== undefined
    ? canEdit
    : campaignInfo.status === statusCampaign.IN_CREATION && !campaignInfo.authorization.readOnly;
  const { t } = useTranslation();
  const { handleCriticalError } = useError();

  useEffect(() => {
    if (!maxSize) {
      documentationServices.getConfiguration()
        .then(setAttachments)
        .catch(handleCriticalError);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const files = (fileList?.attachments?.length > 0 && (
    <div className={styles.AttachmentsList}>
      {fileList.attachments.map((att: Attachment, index: number) => (
          <AttachmentFile
            key={`${att.fullName}-${index}`}
            attachment={att}
            checkDisable={checkDisable}
            canDelete={canDelete ?? canEdit ?? false}
            deleteBlocked={deleteBlocked ?? false}
            onFavorite={setFavorite}
            onDownload={download}
            onDelete={setDeleteFileId}
            isAffectedVehicles={isAffectedVehicles}
            uploadProgress={uploadProgress?.[att.fullName ?? '']}
            onChangeFileTags={onChangeFileTags}
            readOnlyTags={readOnlyTags}
          />
      ))}
    </div>
  ));

  return (
    <div className={styles.PageContent}>
      {showUpload
        ? (
          <>
            <DragDrop id={dragdropId} className={styles.DropZone} onDrop={uploadHandler}>
              <section className={styles.DropLabel}>
                {uploadProgress
                  ? <Spinner isVisible small />
                  : (
                    <>
                      <CloudSVG />
                      <span className={styles.Text}>{t('attachments.dropFilesOr')}</span>
                      <label htmlFor={inputId}>{t('attachments.browse')}</label>
                      <input
                        id={inputId}
                        data-testid={inputId}
                        multiple
                        type="file"
                        accept={allExtensionsAccept}
                        className={styles.InputFile}
                        onChange={(e) => uploadHandler(e.target.files as FileList)}
                      />
                    </>
                  )}
              </section>
              {files}
            </DragDrop>

            {maxSize && (<InfoMsg msg={t('attachments.maxSize', { maxSize, allowedFormats })} />)}
          </>
        )
        : files || t('attachments.noAttachments')}
    </div>
  );
};

const mapStateToProps = (state) => ({
  maxSize: getAttachementForms(state).maxSize,
  allowedFormats: getAttachementForms(state).validExtensions?.join(', '),
  readOnly: getCampaignInfo(state).authorization?.readOnly,
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setAttachments: setAttachmentsForms,
};

export default connect(mapStateToProps, actionCreators)(AttachmentCollection);
